import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { buildMeterIndexMap, readingKey } from "../utils/meterIndex";
import MachineTimeSeriesPanel from "./MachineTimeSeriesPanel";
import { needsRatedPowerAssignment } from "../utils/nodeSettingsLink";
import FieldHelp from "../help/FieldHelp";
import { getFieldHelp } from "../help/registry";
import { GridIcon, SolarIcon, GeneratorIcon } from "./ColorfulIcons";

const isSourceCategory = (cat) =>
  ["GRID", "SOLAR", "GENERATOR"].includes((cat || "").toUpperCase());

const STATUS_COLORS = {
  Normal: "#22c55e",
  Warning: "#f59e0b",
  Critical: "#ef4444",
  OFF: "#94a3b8",
};

// Health status tooltips (based on % of rated power) — easy wording
const HEALTH_TOOLTIPS = {
  Normal: "Machine is running fine. No issues.",
  Warning: "Machine is close to its rated power. Keep an eye on it.",
  Critical: "Machine has crossed its rated power. Needs attention now.",
};

// Light background tints so the whole box reflects the health status
const STATUS_BG = {
  Normal: "#f0fdf4",
  Warning: "#fffbeb",
  Critical: "#fef2f2",
  OFF: "#f8fafc",
};


// Same grey and weight as styles.vLineDown / hLine / vStub, so a link to an
// extra parent is drawn in the tree's own connector style - just inverted.
const SHARED_LINK_COLOR = "#cbd5e1";
const SHARED_LINK_WIDTH = 2;
// Matches styles.vStub, the piece of connector already sitting above every
// child - the bar is drawn level with its top so the two join cleanly.
const STUB_HEIGHT = 14;

const CATEGORY_BADGE = {
  GRID: { label: "Main", bg: "#dbeafe", fg: "#1e40af" },
  SOLAR: { label: "Solar", bg: "#d1fae5", fg: "#065f46" },
  GENERATOR: { label: "Generator", bg: "#fef3c7", fg: "#92400e" },
  VIRTUAL: { label: "Virtual", bg: "#f1f5f9", fg: "#475569" },
  MACHINE: { label: "Machine", bg: "#f1f5f9", fg: "#475569" },
  LOAD: { label: "Load", bg: "#f1f5f9", fg: "#475569" },
};

// Power Factor color thresholds — same as the Live table view.
const pfColor = (pf) => {
  const n = Number(pf);
  if (pf == null || !Number.isFinite(n)) return undefined;
  if (n < 0.7) return "#dc2626"; // red
  if (n <= 0.8) return "#f59e0b"; // orange
  return "#16a34a"; // green
};

const HierarchyTreeView = ({
  hierarchyTree,
  latestData,
  healthStatusData,
  loading,
  error,
  unitPrice = 0,
  username = "",
  onAssignRatedPower,
  expandAllSignal = 0,
  collapseAllSignal = 0,
  onStatusCountsChange,
  distributionMode = false,
  onToggleDistribution,
}) => {
  // Fully responsive viewport fill.
  // Measures container's offset from top via ref + ResizeObserver,
  // CSS calc() acts as fallback before first measurement.
  const containerRef = useRef(null);
  const [availableHeight, setAvailableHeight] = useState(null);

  useEffect(() => {
    const recompute = () => {
      if (!containerRef.current) return;
      const top = containerRef.current.getBoundingClientRect().top;
      const h = window.innerHeight - top - 12;
      if (h > 100) setAvailableHeight(h);
    };
    recompute();
    const raf = requestAnimationFrame(recompute);
    const t1 = setTimeout(recompute, 100);
    const t2 = setTimeout(recompute, 300);
    const t3 = setTimeout(recompute, 800);
    window.addEventListener("resize", recompute);
    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(recompute);
      observer.observe(document.body);
    }
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      window.removeEventListener("resize", recompute);
      if (observer) observer.disconnect();
    };
  }, []);

  // ===== meter index map =====
  // Shared with the table view; see utils/meterIndex.js.
  const meterIndexMap = useMemo(
    () => buildMeterIndexMap(hierarchyTree),
    [hierarchyTree],
  );

  // ===== readings =====
  const readingsMap = useMemo(() => {
    const map = new Map();
    if (!latestData?.readings) return map;
    latestData.readings.forEach((r) => {
      const mid = r.meter_id;
      if (!mid) return;
      const key = r.meter_index != null ? `${mid}_${r.meter_index}` : mid;
      map.set(key, r);
    });
    return map;
  }, [latestData]);

  const getReading = (node) => {
    if (!node?.meter_id) return null;
    const key = readingKey(node.meter_id, meterIndexMap, node.machine_name);
    return readingsMap.get(key) || null;
  };

  const lookupHealth = (mid, name) => {
    if (!healthStatusData || !mid) return null;
    const idx = meterIndexMap.get(name);
    let raw = null;
    if (idx != null && healthStatusData[`${mid}_${idx}`]) {
      raw = healthStatusData[`${mid}_${idx}`];
    } else if (healthStatusData[mid]) {
      raw = healthStatusData[mid];
    } else {
      const keys = Object.keys(healthStatusData);
      const match = keys.find((k) => k === mid || k.startsWith(`${mid}_`));
      raw = match ? healthStatusData[match] : null;
    }
    // Backend shape: object { percent, status }. "-" / anything else → no status.
    if (raw && typeof raw === "object") {
      return { status: raw.status || null, percent: Number(raw.percent) };
    }
    return null;
  };

  // ===== Decorate full tree =====
  const decoratedTree = useMemo(() => {
    if (!hierarchyTree) return null;

    const decorate = (node, path = "", isRoot = false) => {
      // Use whatever category backend sends. If none, no badge.
      const cat = (node.category || "").toUpperCase();
      const reading = getReading(node);
      const directPower = reading?.power_kw ?? null;
      const nodePath = `${path}/${node.machine_name || "?"}`;
      const children = (node.children || []).map((c) => decorate(c, nodePath));
      const isVirtual = !!node.is_virtual || cat === "VIRTUAL";

      let power;
      // The root reports the backend's total, so the root card, the header
      // above it and the Live table all show one figure.
      if (isRoot && latestData?.totalUsage != null) {
        power = latestData.totalUsage;
      } else if (directPower != null) power = directPower;
      else if (isVirtual) {
        // Same rule as the Live table view: take each child's own reading
        // (sources as absolute values) and descend only where there is none.
        const sumChildren = (kids) =>
          kids.reduce((s, c) => {
            if (!c.reading) return s + sumChildren(c.children);
            const pw = parseFloat(c.reading.power_kw) || 0;
            return s + (c.category === "SOLAR" || c.category === "GENERATOR" ? Math.abs(pw) : pw);
          }, 0);
        power = sumChildren(children);
      } else power = 0;

      const isStale = !!reading?.is_stale;
      const threshold = cat === "GENERATOR" ? 0.5 : 0.05;
      const isOff = !isVirtual && Math.abs(power) <= threshold;
      const isExporting = cat === "GRID" && power < -threshold;

      let healthInfo = null;
      if (!isVirtual && !isOff)
        healthInfo = lookupHealth(node.meter_id, node.machine_name);
      let healthStatus = healthInfo?.status || null;
      let healthPercent = healthInfo?.percent ?? null;

      const nodeRatedPower =
        reading?.rated_power !== undefined && reading?.rated_power !== null
          ? Number(reading.rated_power)
          : node.rated_power != null
            ? Number(node.rated_power)
            : null;

      // Handle rated power check for all nodes including source meters (GRID, SOLAR, GENERATOR)
      if (!isOff && !isVirtual && nodeRatedPower && nodeRatedPower > 0) {
        const pct = (Math.abs(power) / nodeRatedPower) * 100;
        if (healthPercent == null || isNaN(healthPercent)) {
          healthPercent = pct;
        }
        if (!healthStatus || healthStatus === "-") {
          if (pct > 100) healthStatus = "Critical";
          else if (pct >= 80) healthStatus = "Warning";
          else healthStatus = "Normal";
        } else if (pct > 100 && healthStatus !== "Critical") {
          // If power exceeds rated capacity, always flag as Critical
          healthStatus = "Critical";
        }
      }

      const statusKey = isOff
        ? "OFF"
        : healthStatus === "Warning"
          ? "Warning"
          : healthStatus === "Critical"
            ? "Critical"
            : healthStatus === "Normal"
              ? "Normal"
              : null;

      // Recursive count of non-virtual descendants and their on/off split
      let total = 0;
      let online = 0;
      const walk = (n) => {
        if (!n.is_virtual) {
          total += 1;
          if (!n.is_off) online += 1;
        }
        n.children.forEach(walk);
      };
      children.forEach(walk);
      const counts = { total, online, offline: total - online };

      const insight_value = reading?.insight_value !== undefined ? reading.insight_value : (node.insight_value ?? null);

      return {
        id: nodePath,
        name: node.machine_name || "Unnamed",
        category: cat,
        is_virtual: isVirtual,
        is_multi_parent: !!node.is_multi_parent || (Array.isArray(node.parent_meter_ids) && node.parent_meter_ids.length > 1),
        parent_meter_ids: node.parent_meter_ids || [],
        primary_parent_meter_id: node.primary_parent_meter_id || null,
        meter_id: node.meter_id || null,
        // Needed to tell "no rated power set" apart from "off right now" —
        // lookupHealth is skipped in both cases, but only the first is fixable.
        rated_power:
          reading?.rated_power !== undefined && reading?.rated_power !== null
            ? Number(reading.rated_power)
            : node.rated_power != null
              ? Number(node.rated_power)
              : null,
        power,
        is_stale: isStale,
        is_exporting: isExporting,
        is_off: isOff,
        status_key: statusKey,
        health_status: healthStatus,
        health_percent: healthPercent,
        reading,
        insight_value,
        children,
        counts,
      };
    };
    const tree = decorate(hierarchyTree, "", true);

    // The type badge means the same thing on every card, so only the first one
    // carries its hover help.
    const markFirstBadged = (n) => {
      if (!n) return false;
      if (CATEGORY_BADGE[n.category]) {
        n.explains_category = true;
        return true;
      }
      return n.children.some(markFirstBadged);
    };
    markFirstBadged(tree);

    return tree;
  }, [hierarchyTree, readingsMap, meterIndexMap, healthStatusData, latestData]);

  // ===== Status counts (recursive) =====
  const statusCounts = useMemo(() => {
    const counts = { Normal: 0, Warning: 0, Critical: 0, Unknown: 0, OFF: 0 };
    if (!decoratedTree) return counts;
    const walk = (n) => {
      if (!n.is_virtual) {
        if (n.is_off) counts.OFF += 1;
        else if (n.status_key === "Normal") counts.Normal += 1;
        else if (n.status_key === "Warning") counts.Warning += 1;
        else if (n.status_key === "Critical") counts.Critical += 1;
        else counts.Unknown += 1;
      }
      n.children.forEach(walk);
    };
    decoratedTree.children.forEach(walk);
    return counts;
  }, [decoratedTree]);

  useEffect(() => {
    if (onStatusCountsChange) {
      onStatusCountsChange(statusCounts);
    }
  }, [statusCounts, onStatusCountsChange]);

  // Multi-parent nodes and the parents they are NOT drawn under. One entry per
  // link, fed to SharedLinkElbows so it can connect the two cards.
  const sharedLinks = useMemo(() => {
    const links = [];
    const seen = new Set();
    const walk = (node) => {
      if (!node) return;
      const childId = node.meter_id;
      const primary = node.primary_parent_meter_id;
      if (childId && node.is_multi_parent && Array.isArray(node.parent_meter_ids)) {
        // The node is visited once per parent, but one junction covers them all.
        if (!seen.has(childId)) {
          seen.add(childId);
          const parentIds = node.parent_meter_ids.filter(Boolean);
          if (parentIds.length > 1) links.push({ childId, primary, parentIds });
        }
      }
      (node.children || []).forEach(walk);
    };
    walk(decoratedTree);
    return links;
  }, [decoratedTree]);

  const totalPower = latestData?.totalUsage ?? decoratedTree?.power ?? 0;

  // ===== Expanded set =====
  const [expanded, setExpanded] = useState(() => new Set());
  const prevRootRef = useRef(null);

  useEffect(() => {
    if (!decoratedTree) return;

    // Only reset expanded state if the root machine changes (e.g., switching branches).
    // Polling data updates leave the user's manual expansions intact.
    if (prevRootRef.current !== decoratedTree.name) {
      prevRootRef.current = decoratedTree.name;

      // Default: "single level nodes" (only root is expanded, children are visible but collapsed)
      setExpanded(new Set([decoratedTree.id]));
    }
  }, [decoratedTree]);

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else {
        next.add(id);
        if (decoratedTree) {
          const findAndExpandParents = (currentNode) => {
            if (currentNode.id === id) {
              (currentNode.children || []).forEach((child) => {
                if (child.is_multi_parent) {
                  const addParentPaths = (n, currentPath = []) => {
                    const p = [...currentPath, n.id];
                    if (n.meter_id === child.meter_id) {
                      p.forEach((pId) => next.add(pId));
                    }
                    (n.children || []).forEach((c) => addParentPaths(c, p));
                  };
                  addParentPaths(decoratedTree);
                }
              });
            }
            (currentNode.children || []).forEach(findAndExpandParents);
          };
          findAndExpandParents(decoratedTree);
        }
      }
      return next;
    });

  const expandAll = () => {
    if (!decoratedTree) return;
    const all = new Set();
    const walk = (n) => {
      all.add(n.id);
      n.children.forEach(walk);
    };
    walk(decoratedTree);
    setExpanded(all);
  };

  const collapseAll = () => {
    if (!decoratedTree) return;
    setExpanded(new Set([decoratedTree.id]));
  };

  useEffect(() => {
    if (expandAllSignal > 0) expandAll();
  }, [expandAllSignal]);

  useEffect(() => {
    if (collapseAllSignal > 0) collapseAll();
  }, [collapseAllSignal]);

  // When toggling distribution mode ON, it works like expandAll (all branches open).
  // Save previous expanded state so turning distribution OFF restores it.
  const prevExpandedBeforeDistribution = useRef(null);
  useEffect(() => {
    if (distributionMode) {
      if (decoratedTree) {
        setExpanded((prev) => {
          prevExpandedBeforeDistribution.current = new Set(prev);
          const all = new Set();
          const walk = (n) => {
            all.add(n.id);
            (n.children || []).forEach(walk);
          };
          walk(decoratedTree);
          return all;
        });
      }
    } else {
      if (prevExpandedBeforeDistribution.current) {
        setExpanded(prevExpandedBeforeDistribution.current);
        prevExpandedBeforeDistribution.current = null;
      }
    }
  }, [distributionMode, decoratedTree]);

  // ===== Details modal state =====
  const [selectedItem, setSelectedItem] = useState(null);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });
  // "tabular" = click a box -> detail panel; "linear" = click a box -> live time-series chart.
  const [viewMode, setViewMode] = useState("tabular");

  // Close modal on outside click
  useEffect(() => {
    if (!selectedItem) return;
    const close = () => setSelectedItem(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [selectedItem]);

  const openDetails = (e, node) => {
    if (node.is_virtual) return; // virtual nodes have no details
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const panelW = 560;
    const panelH = 500;
    let left = rect.right + 10;
    let top = rect.top;
    if (left + panelW > window.innerWidth) left = rect.left - panelW - 10;
    if (left < 10) left = 10;
    if (top + panelH > window.innerHeight) top = window.innerHeight - panelH - 10;
    if (top < 10) top = 10;
    setPopupPos({ top, left });
    setSelectedItem(node);
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.empty}>Loading hierarchy…</div>
      </div>
    );
  }

  if (error || !decoratedTree) {
    return (
      <div style={styles.container}>
        <div style={{ ...styles.empty, color: "#ef4444" }}>
          {error || "No data available"}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        ...styles.container,
        height: availableHeight ?? styles.container.height,
      }}
    >
      {/* ===== Org Chart ===== */}
      <ZoomablePane
        expanded={expanded}
        distributionMode={distributionMode}
        treeKey={hierarchyTree}
        sharedLinks={sharedLinks}
        onExpandAll={expandAll}
        onCollapseAll={collapseAll}
      >
        <OrgNode
          node={decoratedTree}
          depth={0}
          expanded={expanded}
          onToggle={toggle}
          onBoxClick={openDetails}
          unitPrice={unitPrice}
          distributionMode={distributionMode}
          onToggleDistribution={onToggleDistribution}
          totalPower={totalPower}
        />
      </ZoomablePane>

      {/* ===== Details Panel (tabular) / Time-series chart (linear) ===== */}
      {selectedItem && viewMode === "linear" ? (
        <MachineTimeSeriesPanel
          node={selectedItem}
          pos={popupPos}
          username={username}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onClose={() => {
            setSelectedItem(null);
            setViewMode("tabular");
          }}
        />
      ) : selectedItem ? (
        <DetailsPanel
          node={selectedItem}
          pos={popupPos}
          totalPower={totalPower}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onAssignRatedPower={onAssignRatedPower}
          onClose={() => {
            setSelectedItem(null);
            setViewMode("tabular");
          }}
        />
      ) : null}
    </div>
  );
};


/**
 * Connectors from a multi-parent node up to the parents it is not drawn under.
 *
 * The chart draws such a node once, beneath its primary parent. These are the
 * links to its other parents: the same elbow the tree uses everywhere else,
 * mirrored so it rises from the top of the card instead of descending from the
 * bottom of a parent.
 *
 * It lives inside the zoom/pan layer, so coordinates are the tree's natural
 * unscaled ones and the browser transform handles zooming. Positions have to
 * be read from the DOM: the chart is laid out by flexbox, so nothing knows
 * where a card sits until it has rendered.
 */
const SharedLinkElbows = ({ innerRef, zoom, links, deps }) => {
  const [paths, setPaths] = useState([]);

  const measure = useCallback(() => {
    const inner = innerRef.current;
    if (!inner) return;
    if (!links.length) {
      setPaths([]);
      return;
    }

    // Read positions as laid out, with any nudge from a previous pass undone -
    // otherwise each run would measure its own offset and drift.
    const columns = links
      .map((l) => inner.querySelector(`[data-node-col="${CSS.escape(String(l.childId))}"]`))
      .filter(Boolean);
    columns.forEach((col) => {
      col.style.transform = "";
    });

    const base = inner.getBoundingClientRect();
    const actualZoom = inner.offsetWidth > 0 ? base.width / inner.offsetWidth : (zoom || 1);

    const box = (id) => {
      if (!id) return null;
      const sid = String(id);
      const el =
        inner.querySelector(`[data-node-box="${CSS.escape(sid)}"]`) ||
        inner.querySelector(`[data-node-col="${CSS.escape(sid)}"]`);
      if (!el) return null;
      const r = el.getBoundingClientRect();
      const left = (r.left - base.left) / actualZoom;
      const top = (r.top - base.top) / actualZoom;
      const width = r.width / actualZoom;
      const height = r.height / actualZoom;
      return { cx: left + width / 2, top, bottom: top + height, width, height };
    };

    const next = [];
    links.forEach(({ childId, parentIds }) => {
      const child = box(childId);
      const parents = parentIds
        .map((id) => box(id))
        .filter(Boolean);
      if (!child || parents.length < 2) return; // some parent is collapsed away

      const maxParentBottom = Math.max(...parents.map((p) => p.bottom));
      if (maxParentBottom >= child.top) return; // no room for the bar

      // Place horizontal bus bar midway between parents' bottoms and child's top
      const barY = Math.round((maxParentBottom + child.top) / 2);

      // Sit the node midway between the parents feeding it, so the junction is
      // symmetrical like every other fan-out in the chart.
      const xs = parents.map((p) => p.cx);
      const mid = (Math.min(...xs) + Math.max(...xs)) / 2;
      const col = inner.querySelector(`[data-node-col="${CSS.escape(String(childId))}"]`);
      if (col) col.style.transform = `translateX(${mid - child.cx}px)`;

      const d = [
        `M ${Math.min(...xs)} ${barY} H ${Math.max(...xs)}`, // the bar
        ...parents.map((p) => `M ${p.cx} ${p.bottom} V ${barY}`), // down from each parent
        `M ${mid} ${barY} V ${child.top}`, // down into the node
      ].join(" ");
      next.push({ key: String(childId), d, mid, top: child.top });
    });
    setPaths(next);
  }, [innerRef, zoom, links]);

  useEffect(() => {
    // Let flexbox settle before reading positions, and re-measure a few times:
    // card contents (power values) can reflow after the first paint.
    const timers = [0, 80, 260, 600].map((ms) => setTimeout(measure, ms));
    window.addEventListener("resize", measure);
    let ro = null;
    if (typeof ResizeObserver !== "undefined" && innerRef.current) {
      ro = new ResizeObserver(measure);
      ro.observe(innerRef.current);
    }
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener("resize", measure);
      if (ro) ro.disconnect();
      const cols = links
        .map((l) => innerRef.current?.querySelector(`[data-node-col="${CSS.escape(String(l.childId))}"]`))
        .filter(Boolean);
      cols.forEach((col) => {
        col.style.transform = "";
      });
    };
  }, [measure, deps, links, innerRef]);

  if (!paths.length) return null;

  return (
    <svg
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        overflow: "visible",
        pointerEvents: "none",
        zIndex: 1,
      }}
    >
      {paths.map((p) => (
        <g key={p.key}>
          <path
            d={p.d}
            fill="none"
            stroke={SHARED_LINK_COLOR}
            strokeWidth={SHARED_LINK_WIDTH}
          />
          <path
            d={p.d}
            fill="none"
            stroke="#0284c7"
            strokeWidth={2.5}
            strokeDasharray="4 3"
            style={{ animation: "flowDashDown 1.2s linear infinite" }}
          />
          {p.mid != null && p.top != null && (
            <polygon
              points={`${p.mid},${p.top - 2} ${p.mid - 3.5},${p.top - 7} ${p.mid + 3.5},${p.top - 7}`}
              fill="#0284c7"
              style={{ opacity: 0.95 }}
            />
          )}
        </g>
      ))}
    </svg>
  );
};

// ===== Zoomable / pannable pane =====
const ZoomablePane = ({
  children,
  treeKey,
  sharedLinks = [],
  onExpandAll,
  onCollapseAll,
  expanded,
  distributionMode,
}) => {
  const wrapRef = useRef(null);
  const innerRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);

  // Compute fit-to-screen zoom
  const fitToScreen = useCallback(() => {
    if (!wrapRef.current || !innerRef.current) return;
    const cw = wrapRef.current.clientWidth - 48;
    const ch = wrapRef.current.clientHeight - 80;
    const iw = innerRef.current.scrollWidth;
    const ih = innerRef.current.scrollHeight;
    if (iw <= 0 || ih <= 0) return;
    const fitW = cw / iw;
    const fitH = ch / ih;
    const next = Math.min(fitW, fitH, 1.0);
    // Never zoom out to an unreadable speck: clamp minimum to 0.65 (65%)
    setZoom(Math.max(0.65, Math.min(1.2, next)));
    setPan({ x: 0, y: 0 });
  }, []);

  // ONLY auto-fit when the treeKey changes (i.e. client or branch switch)
  useEffect(() => {
    setPan({ x: 0, y: 0 });
    const t = setTimeout(fitToScreen, 120);
    return () => clearTimeout(t);
  }, [treeKey, fitToScreen]);

  const zoomIn = () => {
    setZoom((z) => Math.min(2, Math.round((z + 0.1) * 10) / 10));
  };
  const zoomOut = () => {
    setZoom((z) => Math.max(0.4, Math.round((z - 0.1) * 10) / 10));
  };
  const handleFit = () => {
    fitToScreen();
  };

  // Drag-to-pan
  const onMouseDown = (e) => {
    if (e.target.closest("[data-no-pan]")) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: pan.x,
      origY: pan.y,
    };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setPan({ x: dragRef.current.origX + dx, y: dragRef.current.origY + dy });
  };
  const onMouseUp = () => {
    dragRef.current = null;
  };

  // Ctrl+wheel / pinch zoom
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const handler = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const delta = e.deltaY < 0 ? 0.05 : -0.05;
      setZoom((z) => Math.max(0.4, Math.min(2, z + delta)));
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, []);

  return (
    <div style={styles.zoomOuter}>
      {/* Zoom controls on bottom-left */}
      <div style={styles.zoomBar} data-no-pan>
        <button style={styles.zoomBtn} onClick={zoomOut} title="Zoom out">
          −
        </button>
        <span style={styles.zoomPct}>{Math.round(zoom * 100)}%</span>
        <button style={styles.zoomBtn} onClick={zoomIn} title="Zoom in">
          +
        </button>
        <button
          style={{ ...styles.zoomBtn, ...styles.zoomBtnText }}
          onClick={handleFit}
          title="Fit to screen"
        >
          Fit
        </button>
        {onExpandAll && (
          <>
            <span style={{ width: 1, height: 14, background: "#cbd5e1", margin: "0 3px" }} />
            <button
              style={{ ...styles.zoomBtn, ...styles.zoomBtnText }}
              onClick={onExpandAll}
              title="Expand all nodes"
            >
              Expand all
            </button>
            <button
              style={{ ...styles.zoomBtn, ...styles.zoomBtnText }}
              onClick={onCollapseAll}
              title="Collapse all nodes"
            >
              Collapse all
            </button>
          </>
        )}
      </div>

      <div
        ref={wrapRef}
        style={{
          ...styles.chartWrap,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          position: "relative",
          overflow: "hidden",
        }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        <div
          ref={innerRef}
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: "top center",
            transition: dragRef.current ? "none" : "transform 0.18s cubic-bezier(0.2, 0, 0, 1)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            userSelect: "none",
            willChange: "transform",
            minWidth: "fit-content",
          }}
        >
          <SharedLinkElbows
            innerRef={innerRef}
            zoom={zoom}
            links={sharedLinks}
            deps={`${treeKey}_${expanded ? expanded.size : 0}_${distributionMode}_${zoom}`}
          />
          <div style={{ position: "relative", zIndex: 2, minWidth: "fit-content" }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== Count chip =====
const CountChip = ({ color, label, count, helpId }) => (
  <div style={styles.countChip}>
    <span style={{ ...styles.countDot, background: color }} />
    <span style={styles.countLabel}>
      <FieldHelp id={helpId}>{label}</FieldHelp>
    </span>
    <strong style={{ ...styles.countNum, color }}>{count}</strong>
  </div>
);

// A child fed by several parents gets its connector drawn by SharedLinkElbows
// as one junction, so the ordinary stub above it and duplicate parent feeder lines are suppressed.
const isSharedChild = (child, parent) => {
  if (!child || !parent || !child.is_multi_parent) return false;
  const pIds = Array.isArray(child.parent_meter_ids)
    ? child.parent_meter_ids.filter(Boolean).map(String)
    : [];
  if (pIds.length <= 1) return false;

  const parentMid = String(parent.meter_id || "").trim();
  const parentId = String(parent.id || "").trim();
  const parentName = String(parent.name || "").trim();

  const primaryId = String(child.primary_parent_meter_id || pIds[0] || "").trim();

  return (
    (primaryId && (primaryId === parentMid || primaryId === parentId || primaryId === parentName)) ||
    (parentMid && pIds.includes(parentMid)) ||
    (parentId && pIds.includes(parentId))
  );
};

// Sleek Energy Distribution SVG Icon (power feeder branching to distributed loads)
const DistributionIcon = ({ color = "#0d9488" }) => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 16 16"
    fill="none"
    style={{ display: "block", flexShrink: 0 }}
  >
    {/* Branching distribution circuit lines */}
    <path
      d="M8 5V8M8 8H3.5V11M8 8H12.5V11M8 8V11"
      stroke={color}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* Source feeder node (top) */}
    <circle cx="8" cy="3.5" r="2.2" fill={color} />
    {/* Distributed load nodes (bottom) */}
    <circle cx="3.5" cy="12.5" r="1.5" fill={color} />
    <circle cx="8" cy="12.5" r="1.5" fill={color} />
    <circle cx="12.5" cy="12.5" r="1.5" fill={color} />
  </svg>
);

// ===== Compact Distribution Toggle Component for Root Node =====
const DistributionToggle = ({ active, onToggle }) => {
  const iconColor = active ? "#2dd4bf" : "#0f766e";
  return (
    <div
      role="switch"
      aria-checked={active}
      onClick={(e) => {
        e.stopPropagation();
        onToggle?.();
      }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3.5,
        background: active ? "#042f2e" : "#f8fafc",
        border: `1px solid ${active ? "#0d9488" : "#cbd5e1"}`,
        borderRadius: 999,
        padding: "1.5px 4.5px",
        cursor: "pointer",
        userSelect: "none",
        boxShadow: active
          ? "0 1px 4px rgba(13, 148, 136, 0.3)"
          : "0 1px 2px rgba(0,0,0,0.03)",
        transition: "all 0.15s ease",
        flexShrink: 0,
        height: 18,
        boxSizing: "border-box",
      }}
      title={
        active
          ? "Distribution view ON: Showing power sources & compact load nodes. Click to switch to standard view."
          : "Distribution view OFF: Click to isolate power sources (Grid, Solar, Gen) & compact load nodes."
      }
    >
      <DistributionIcon color={iconColor} />
      <span
        style={{
          fontSize: 9.5,
          fontWeight: 700,
          color: active ? "#f0fdfa" : "#334155",
          letterSpacing: 0.1,
          lineHeight: 1,
        }}
      >
        Distribution
      </span>
      {/* Miniature Switch Track */}
      <span
        style={{
          display: "inline-block",
          width: 15,
          height: 8,
          borderRadius: 999,
          background: active ? "#14b8a6" : "#cbd5e1",
          position: "relative",
          transition: "background 0.2s ease",
          marginLeft: 1,
          flexShrink: 0,
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: 5,
            height: 5,
            borderRadius: "50%",
            background: "#ffffff",
            position: "absolute",
            top: 1.5,
            left: active ? 8.5 : 1.5,
            transition: "left 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
          }}
        />
      </span>
    </div>
  );
};

// ===== Compact Dot Node for Machines in Distribution Mode =====
const DotNode = ({ node, onBoxClick, totalPower = 0 }) => {
  const [hovered, setHovered] = useState(false);
  const isOff = node.is_off || node.is_stale || Math.abs(node.power || 0) <= 0.05;
  const statusKey = isOff ? "OFF" : (node.status_key || "Normal");
  const color = STATUS_COLORS[statusKey] || "#94a3b8";
  const powerKw = Math.abs(node.power || 0);
  const pct = totalPower > 0 ? ((powerKw / totalPower) * 100).toFixed(1) : 0;
  const clickable = !node.is_virtual;
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;

  return (
    <div
      data-node-box={node.meter_id || node.id}
      onClick={clickable ? (e) => onBoxClick && onBoxClick(e, node) : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: clickable ? "pointer" : "default",
        position: "relative",
        padding: "3px",
        userSelect: "none",
        width: 16,
        height: 16,
        boxSizing: "border-box",
        zIndex: hovered ? 99999 : 2,
      }}
    >
      {/* Sleek Indicator Dot - Minimal 10px circular node */}
      <div
        style={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          backgroundColor: isOff ? "#cbd5e1" : color,
          border: "2px solid #ffffff",
          boxShadow: hovered
            ? `0 0 10px ${color}, 0 2px 6px rgba(0,0,0,0.3)`
            : !isOff
              ? `0 0 5px ${color}88, 0 1px 2px rgba(0,0,0,0.12)`
              : "0 1px 2px rgba(0,0,0,0.08)",
          transform: hovered ? "scale(1.4)" : "scale(1)",
          transition: "all 0.18s cubic-bezier(0.4, 0, 0.2, 1)",
          flexShrink: 0,
        }}
      />

      {/* Floating Hover Tooltip Card */}
      {hovered && (
        <div
          style={{
            position: "absolute",
            ...(hasChildren
              ? { bottom: "calc(100% + 9px)" }
              : { top: "calc(100% + 9px)" }),
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#0f172a",
            color: "#f8fafc",
            borderRadius: 8,
            padding: "8px 12px",
            boxShadow: "0 12px 30px rgba(0,0,0,0.6), 0 2px 8px rgba(0,0,0,0.3)",
            border: "1px solid rgba(255, 255, 255, 0.15)",
            zIndex: 999999,
            minWidth: 160,
            pointerEvents: "none",
            textAlign: "left",
            fontSize: 11,
            whiteSpace: "nowrap",
          }}
        >
          {/* Arrow Caret */}
          <div
            style={{
              position: "absolute",
              ...(hasChildren
                ? {
                    bottom: -5,
                    borderLeft: "5px solid transparent",
                    borderRight: "5px solid transparent",
                    borderTop: "5px solid #0f172a",
                  }
                : {
                    top: -5,
                    borderLeft: "5px solid transparent",
                    borderRight: "5px solid transparent",
                    borderBottom: "5px solid #0f172a",
                  }),
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
            }}
          />
          <div style={{ fontWeight: 700, color: "#ffffff", marginBottom: 5, fontSize: 12 }}>
            {node.name}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 14, color: "#94a3b8", fontSize: 10.5 }}>
            <span>Power:</span>
            <span style={{ color: "#38bdf8", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
              {powerKw.toFixed(2)} kW ({pct}%)
            </span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 14, color: "#94a3b8", fontSize: 10.5, marginTop: 3 }}>
            <span>Status:</span>
            <span style={{ color: isOff ? "#94a3b8" : color, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: isOff ? "#94a3b8" : color, display: "inline-block" }} />
              {statusKey}
            </span>
          </div>
          {node.category && (
            <div style={{ display: "flex", justifyContent: "space-between", gap: 14, color: "#94a3b8", fontSize: 10, marginTop: 3 }}>
              <span>Category:</span>
              <span style={{ color: "#cbd5e1", fontWeight: 600 }}>{node.category}</span>
            </div>
          )}
          {clickable && (
            <div style={{ marginTop: 6, paddingTop: 4, borderTop: "1px solid #334155", fontSize: 9.5, color: "#38bdf8", textAlign: "center", fontWeight: 600 }}>
              Click to view live trends & details
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ===== Recursive org chart node =====
// Renders: [Box] -> if expanded and has children -> | (down line) -> ┬─── (h line) -> [Children with stubs]
const OrgNode = ({
  node,
  depth,
  expanded,
  onToggle,
  onBoxClick,
  unitPrice = 0,
  distributionMode = false,
  onToggleDistribution,
  totalPower = 0,
}) => {
  // A node belonging to several parents is drawn once, under its primary parent.
  // Under secondary parents it is filtered out so it doesn't duplicate.
  const drawnChildren = useMemo(() => {
    return (node.children || []).filter((child) => {
      if (!child.is_multi_parent) return true;
      const pIds = Array.isArray(child.parent_meter_ids)
        ? child.parent_meter_ids.filter(Boolean).map(String)
        : [];
      if (pIds.length <= 1) return true;

      const primaryId = String(child.primary_parent_meter_id || pIds[0] || "").trim();
      const parentMid = String(node.meter_id || "").trim();
      const parentId = String(node.id || "").trim();
      const parentName = String(node.name || "").trim();

      const isPrimary =
        (primaryId && (primaryId === parentMid || primaryId === parentId || primaryId === parentName));
      return isPrimary;
    });
  }, [node.children, node.meter_id, node.id, node.name]);

  const nonSharedIndices = useMemo(() => {
    const indices = [];
    drawnChildren.forEach((child, i) => {
      if (!isSharedChild(child, node)) indices.push(i);
    });
    return indices;
  }, [drawnChildren, node]);

  const hasChildren = drawnChildren.length > 0;
  const isOpen = expanded.has(node.id);
  const isRoot = depth === 0;
  const childCount = drawnChildren.length;

  const parentBoxRef = useRef(null);
  const childrenRowRef = useRef(null);
  const childRefs = useRef([]);
  const [busLayout, setBusLayout] = useState({
    ready: false,
    firstCenter: 0,
    lastCenter: 0,
    parentX: 0,
    leftmostActiveX: null,
    rightmostActiveX: null,
    centers: [],
  });

  const measure = useCallback(() => {
    const row = childrenRowRef.current;
    if (!row || childCount < 1) return;
    const rowRect = row.getBoundingClientRect();
    if (rowRect.width === 0 || row.offsetWidth === 0) return;

    // Actual scale factor applied by CSS transforms on ancestors
    const actualZoom = rowRect.width / row.offsetWidth;

    const centers = [];
    for (let i = 0; i < childCount; i++) {
      const el = childRefs.current[i];
      if (el) {
        // el.offsetLeft + el.offsetWidth / 2 is in pure local unscaled CSS pixels of row
        const center = el.offsetLeft + el.offsetWidth / 2;
        centers.push(center);
      }
    }
    if (centers.length === 0) return;

    const firstCenter = centers[0];
    const lastCenter = centers[centers.length - 1];

    let pX = row.offsetWidth / 2;
    if (parentBoxRef.current) {
      const pRect = parentBoxRef.current.getBoundingClientRect();
      pX = (pRect.left + pRect.width / 2 - rowRect.left) / actualZoom;
    }

    let leftActive = null;
    let rightActive = null;

    drawnChildren.forEach((child, i) => {
      const isChildActive = !child.is_off && !child.is_stale && Math.abs(child.power || 0) > 0.05;
      if (isChildActive && centers[i] != null && !isSharedChild(child, node)) {
        const cX = centers[i];
        if (cX <= pX + 2) {
          if (leftActive === null || cX < leftActive) leftActive = cX;
        }
        if (cX >= pX - 2) {
          if (rightActive === null || cX > rightActive) rightActive = cX;
        }
      }
    });

    setBusLayout({
      ready: true,
      firstCenter,
      lastCenter,
      parentX: pX,
      leftmostActiveX: leftActive,
      rightmostActiveX: rightActive,
      centers,
    });
  }, [childCount, drawnChildren, node]);

  useEffect(() => {
    if (!isOpen || childCount < 1) return;
    measure();
    const raf1 = requestAnimationFrame(measure);
    const t1 = setTimeout(measure, 40);
    const t2 = setTimeout(measure, 120);
    const t3 = setTimeout(measure, 300);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && childrenRowRef.current) {
      ro = new ResizeObserver(() => {
        measure();
      });
      ro.observe(childrenRowRef.current);
      childRefs.current.forEach((ref) => {
        if (ref) ro.observe(ref);
      });
      if (parentBoxRef.current) ro.observe(parentBoxRef.current);
    }

    return () => {
      cancelAnimationFrame(raf1);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      if (ro) ro.disconnect();
    };
  }, [isOpen, childCount, expanded, distributionMode, measure]);

  // Flow parameters for parent connector
  const isParentFlowing = Math.abs(node.power) > 0.05 && !node.is_off && !node.is_stale;
  const parentSpeed = Math.max(0.6, Math.min(2.2, 2.2 - (Math.abs(node.power) / (totalPower || 100)) * 1.4));
  const parentCat = (node.category || "").toUpperCase();
  const parentColor = parentCat === "SOLAR" ? "#10b981" : parentCat === "GENERATOR" ? "#f59e0b" : parentCat === "GRID" ? "#0284c7" : "#0ea5e9";

  const isSource = isSourceCategory(node.category);
  // In distribution mode, all non-root, non-source nodes become dots (even intermediate nodes)
  const shouldRenderAsDot = distributionMode && !isRoot && !isSource;

  return (
    <div style={styles.orgNodeWrap}>
      {/* Dynamic Keyframes injected once per chart */}
      {isRoot && (
        <style>{`
          @keyframes flowDashDown {
            from { stroke-dashoffset: 14; }
            to { stroke-dashoffset: 0; }
          }
          @keyframes flowDashUp {
            from { stroke-dashoffset: 0; }
            to { stroke-dashoffset: 14; }
          }
          @keyframes flowDashLeft {
            from { stroke-dashoffset: 0; }
            to { stroke-dashoffset: 14; }
          }
          @keyframes flowDashRight {
            from { stroke-dashoffset: 14; }
            to { stroke-dashoffset: 0; }
          }
        `}</style>
      )}

      {/* The box: DotNode when in distributionMode for non-source machines, 3D icon for sources, or NodeBox for others */}
      <div ref={parentBoxRef} style={{ display: "flex", justifyContent: "center" }}>
        {shouldRenderAsDot ? (
          <DotNode
            node={node}
            onBoxClick={(e) => onBoxClick && onBoxClick(e, node)}
            totalPower={totalPower}
          />
        ) : isSource ? (
          <SourceNode3D
            node={node}
            isRoot={isRoot}
            canToggle={hasChildren}
            isOpen={isOpen}
            onBoxClick={(e) => onBoxClick && onBoxClick(e, node)}
            onToggleClick={(e) => {
              e.stopPropagation();
              if (hasChildren) onToggle(node.id);
            }}
            unitPrice={unitPrice}
            distributionMode={distributionMode}
            onToggleDistribution={onToggleDistribution}
            totalPower={totalPower}
          />
        ) : (
          <NodeBox
            node={node}
            isRoot={isRoot}
            canToggle={hasChildren}
            isOpen={isOpen}
            onBoxClick={(e) => onBoxClick && onBoxClick(e, node)}
            onToggleClick={(e) => {
              e.stopPropagation();
              if (hasChildren) onToggle(node.id);
            }}
            unitPrice={unitPrice}
            distributionMode={distributionMode}
            onToggleDistribution={onToggleDistribution}
            totalPower={totalPower}
          />
        )}
      </div>

      {/* Children subtree with Dynamic Animated Flow Lines & Directional Arrows */}
      {hasChildren && isOpen && (
        <div
          ref={childrenRowRef}
          style={{
            ...styles.childrenRow,
            marginTop: 22,
            justifyContent: childCount === 1 ? "center" : "space-around",
          }}
        >
          {/* Connector Overlay SVG: exactly spans the 22px connector gap directly above childrenRow */}
          <svg
            style={{
              position: "absolute",
              top: -22,
              left: 0,
              width: "100%",
              height: 24,
              overflow: "visible",
              pointerEvents: "none",
              zIndex: 0,
            }}
          >
            {busLayout.ready && nonSharedIndices.length > 0 && (
              <>
                {/* 1. Parent Feeder Line Down into the bus bar */}
                <line
                  x1={busLayout.parentX}
                  y1={0}
                  x2={nonSharedIndices.length === 1 ? busLayout.centers[nonSharedIndices[0]] : busLayout.parentX}
                  y2={22}
                  stroke="#cbd5e1"
                  strokeWidth="2"
                />
                {isParentFlowing && (
                  <>
                    <line
                      x1={busLayout.parentX}
                      y1={0}
                      x2={nonSharedIndices.length === 1 ? busLayout.centers[nonSharedIndices[0]] : busLayout.parentX}
                      y2={22}
                      stroke={parentColor}
                      strokeWidth="2.5"
                      strokeDasharray="4 3"
                      style={{ animation: `flowDashDown ${parentSpeed}s linear infinite` }}
                    />
                    {/* Directional Arrow pointing DOWN into bus */}
                    <polygon
                      points={`${nonSharedIndices.length === 1 ? busLayout.centers[nonSharedIndices[0]] : busLayout.parentX},19 ${(nonSharedIndices.length === 1 ? busLayout.centers[nonSharedIndices[0]] : busLayout.parentX) - 3.5},13 ${(nonSharedIndices.length === 1 ? busLayout.centers[nonSharedIndices[0]] : busLayout.parentX) + 3.5},13`}
                      fill={parentColor}
                      style={{ opacity: 0.95 }}
                    />
                  </>
                )}

                {/* 2. Continuous Horizontal Distribution Bus Bar (if nonSharedIndices.length > 1) */}
                {nonSharedIndices.length > 1 && (
                  <>
                    {/* Unbroken solid neutral base busbar across non-shared children span */}
                    <line
                      x1={busLayout.centers[nonSharedIndices[0]]}
                      y1={22}
                      x2={busLayout.centers[nonSharedIndices[nonSharedIndices.length - 1]]}
                      y2={22}
                      stroke="#cbd5e1"
                      strokeWidth="2"
                    />

                    {/* Active flow segments only where power actually travels! */}
                    {isParentFlowing && (
                      <>
                        {/* Leftward active flow from parentX to leftmost active child */}
                        {busLayout.leftmostActiveX !== null && busLayout.leftmostActiveX < busLayout.parentX - 2 && (
                          <line
                            x1={busLayout.parentX}
                            y1={22}
                            x2={busLayout.leftmostActiveX}
                            y2={22}
                            stroke={parentColor}
                            strokeWidth="2.5"
                            strokeDasharray="4 3"
                            style={{ animation: `flowDashLeft ${parentSpeed}s linear infinite` }}
                          />
                        )}

                        {/* Rightward active flow from parentX to rightmost active child */}
                        {busLayout.rightmostActiveX !== null && busLayout.rightmostActiveX > busLayout.parentX + 2 && (
                          <line
                            x1={busLayout.parentX}
                            y1={22}
                            x2={busLayout.rightmostActiveX}
                            y2={22}
                            stroke={parentColor}
                            strokeWidth="2.5"
                            strokeDasharray="4 3"
                            style={{ animation: `flowDashRight ${parentSpeed}s linear infinite` }}
                          />
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </svg>

          {/* Child columns with vertical stubs */}
          {drawnChildren.map((child, idx) => {
            const isChildActive = !child.is_off && !child.is_stale && Math.abs(child.power || 0) > 0.05;
            const childSpeed = Math.max(0.6, Math.min(2.2, 2.2 - (Math.abs(child.power || 0) / (totalPower || 100)) * 1.4));
            const childCat = (child.category || "").toUpperCase();
            const childColor = childCat === "SOLAR" ? "#10b981" : childCat === "GENERATOR" ? "#f59e0b" : childCat === "GRID" ? "#0284c7" : "#0ea5e9";
            const isShared = isSharedChild(child, node);
            const isChildSupplying = childCat === "SOLAR" || childCat === "GENERATOR" || (childCat === "GRID" && !child.is_exporting);

            return (
              <div
                key={child.id}
                ref={(el) => (childRefs.current[idx] = el)}
                data-node-col={child.meter_id || child.id}
                style={styles.childCol}
              >
                {/* Dynamic vertical stub above each child */}
                <div
                  style={{
                    width: 10,
                    height: 18,
                    position: "relative",
                    display: "flex",
                    justifyContent: "center",
                    visibility: isShared ? "hidden" : "visible",
                    flexShrink: 0,
                    zIndex: 1,
                  }}
                >
                  <svg width="10" height="18" style={{ display: "block", overflow: "visible" }}>
                    {/* Neutral base line: overlaps busbar at y=0 */}
                    <line x1="5" y1="0" x2="5" y2="18" stroke="#cbd5e1" strokeWidth="2" />
                    {/* Active line and arrow ONLY if child is active and flowing */}
                    {isChildActive && (
                      <>
                        <line
                          x1="5"
                          y1="0"
                          x2="5"
                          y2="18"
                          stroke={childColor}
                          strokeWidth="2.5"
                          strokeDasharray="4 3"
                          style={{
                            animation: isChildSupplying
                              ? `flowDashUp ${childSpeed}s linear infinite`
                              : `flowDashDown ${childSpeed}s linear infinite`,
                          }}
                        />
                        {/* Directional Flow Arrow: UP if supplying into bus, DOWN if consuming */}
                        {isChildSupplying ? (
                          <polygon
                            points="5,3 2,9 8,9"
                            fill={childColor}
                            style={{ opacity: 0.95 }}
                          />
                        ) : (
                          <polygon
                            points="5,15 2,9 8,9"
                            fill={childColor}
                            style={{ opacity: 0.95 }}
                          />
                        )}
                      </>
                    )}
                  </svg>
                </div>

                <OrgNode
                  node={child}
                  depth={depth + 1}
                  expanded={expanded}
                  onToggle={onToggle}
                  onBoxClick={onBoxClick}
                  unitPrice={unitPrice}
                  distributionMode={distributionMode}
                  onToggleDistribution={onToggleDistribution}
                  totalPower={totalPower}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ===== 3D Source Node Component (GRID, SOLAR, GENERATOR) =====
const SourceNode3D = ({
  node,
  isRoot,
  canToggle,
  isOpen,
  onBoxClick,
  onToggleClick,
  unitPrice = 0,
  distributionMode = false,
  onToggleDistribution,
  totalPower = 0,
}) => {
  const cat = (node.category || "").toUpperCase();
  const clickable = !node.is_virtual;
  const isGrid = cat === "GRID";
  const isSolar = cat === "SOLAR";

  const theme = isGrid
    ? {
        label: "Grid",
        img: "/images/dashboard/3d_grid.jpg",
        accent: "#38bdf8",
        glow: "rgba(2, 132, 199, 0.35)",
        badgeBg: "#e0f2fe",
        badgeFg: "#0369a1",
      }
    : isSolar
      ? {
          label: "Solar",
          img: "/images/dashboard/3d_solar.jpg",
          accent: "#fde047",
          glow: "rgba(245, 158, 11, 0.35)",
          badgeBg: "#fef3c7",
          badgeFg: "#92400e",
        }
      : {
          label: "Generator",
          img: "/images/dashboard/3d_generator.jpg",
          accent: "#fca5a5",
          glow: "rgba(239, 68, 68, 0.35)",
          badgeBg: "#fee2e2",
          badgeFg: "#991b1b",
        };

  const nodeRatedPower =
    node.rated_power != null
      ? Number(node.rated_power)
      : node.reading?.rated_power != null
        ? Number(node.reading.rated_power)
        : null;

  // Critical overload indicator for source meters (taking into account rated power)
  const isCritical =
    node.status_key === "Critical" ||
    node.health_status === "Critical" ||
    (nodeRatedPower && nodeRatedPower > 0 && Math.abs(node.power) > nodeRatedPower);
  const healthPercent =
    node.health_percent != null
      ? Number(node.health_percent)
      : nodeRatedPower && nodeRatedPower > 0
        ? (Math.abs(node.power) / nodeRatedPower) * 100
        : null;

  return (
    <div
      data-node-box={node.meter_id || node.id}
      onClick={clickable ? onBoxClick : undefined}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 12,
        cursor: clickable ? "pointer" : "default",
        padding: "4px 6px",
        position: "relative",
        userSelect: "none",
        transition: "transform 0.15s ease",
      }}
      onMouseEnter={(e) => {
        if (clickable) e.currentTarget.style.transform = "translateY(-2px) scale(1.02)";
      }}
      onMouseLeave={(e) => {
        if (clickable) e.currentTarget.style.transform = "translateY(0) scale(1)";
      }}
    >
      {/* 3D Icon Graphic - Unboxed with ambient 3D depth */}
      <div
        style={{
          position: "relative",
          width: 66,
          height: 66,
          flexShrink: 0,
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: isCritical
            ? "0 0 0 3px #ef4444, 0 0 20px 6px rgba(239, 68, 68, 0.55), 0 8px 18px rgba(0,0,0,0.2)"
            : node.is_off
              ? "0 4px 10px rgba(0,0,0,0.12)"
              : `0 8px 22px -2px ${theme.glow}, 0 2px 6px rgba(0,0,0,0.08)`,
          transition: "box-shadow 0.2s ease",
        }}
      >
        <img
          src={theme.img}
          alt={theme.label}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
            filter: node.is_off ? "grayscale(90%) opacity(0.65)" : "none",
          }}
        />
        {/* Overload alert dot on 3D icon */}
        {isCritical && (
          <span
            style={{
              position: "absolute",
              top: 3,
              right: 3,
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: "#ef4444",
              border: "2px solid #ffffff",
              boxShadow: "0 0 8px #ef4444",
            }}
            title="Critical: Overloaded above rated power!"
          />
        )}
      </div>

      {/* Floating Info Pill next to 3D icon */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          background: "rgba(255, 255, 255, 0.96)",
          backdropFilter: "blur(10px)",
          borderRadius: 12,
          border: `1.5px solid ${isCritical ? "#ef4444" : node.is_off ? "#cbd5e1" : theme.accent}`,
          boxShadow: isCritical
            ? "0 0 0 2px rgba(239, 68, 68, 0.25), 0 4px 14px rgba(239, 68, 68, 0.15)"
            : "0 4px 14px rgba(15, 23, 42, 0.08)",
          padding: "7px 12px",
          minWidth: 135,
          maxWidth: 190,
          textAlign: "left",
        }}
      >
        {/* Name & Type Badge + Distribution Toggle if Root */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, marginBottom: 2 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4, overflow: "hidden" }}>
            <span
              style={{
                fontWeight: 700,
                fontSize: 13,
                color: "#0f172a",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 95,
              }}
              title={node.name}
            >
              {node.name}
            </span>
            <span
              style={{
                fontSize: 9,
                fontWeight: 800,
                padding: "1px 5px",
                borderRadius: 4,
                background: theme.badgeBg,
                color: theme.badgeFg,
                textTransform: "uppercase",
                letterSpacing: 0.3,
              }}
            >
              {theme.label}
            </span>
          </div>

          {isRoot && (
            <DistributionToggle active={distributionMode} onToggle={onToggleDistribution} />
          )}
        </div>

        {/* Power in kW and PKR Operating Cost */}
        <div style={{ display: "flex", alignItems: "baseline", gap: 3, flexWrap: "wrap" }}>
          <span
            style={{
              fontWeight: 800,
              fontSize: isRoot ? 20 : 16,
              color: node.is_exporting ? "#10b981" : node.is_off ? "#94a3b8" : "#0f172a",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {Math.abs(node.power).toFixed(2)}
          </span>
          <span style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>kW</span>
          {node.is_exporting && (
            <span
              style={{
                fontSize: 8.5,
                fontWeight: 700,
                background: "#d1fae5",
                color: "#065f46",
                padding: "1px 4px",
                borderRadius: 4,
                marginLeft: 2,
              }}
            >
              ↑ EXPORT
            </span>
          )}

          {/* Operating Cost / hr on Topmost Root Node next to kW */}
          {isRoot && (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                fontSize: "10px",
                color: "#0f766e",
                background: "#f0fdfa",
                border: "1px solid #ccfbf1",
                padding: "1px 6px",
                borderRadius: "5px",
                fontWeight: 600,
                marginLeft: 4,
                fontVariantNumeric: "tabular-nums",
              }}
              title={unitPrice > 0 ? `Estimated operating cost: PKR ${unitPrice} per kWh` : "Unit price not set in settings"}
            >
              <span style={{ fontSize: "9px", color: "#14b8a6", fontWeight: 700 }}>PKR</span>
              <span>
                {unitPrice > 0 && Math.abs(node.power) > 0
                  ? (Math.abs(node.power) * unitPrice).toLocaleString(undefined, {
                      maximumFractionDigits: 0,
                    })
                  : "--"}
              </span>
              <span style={{ fontSize: "8.5px", color: "#64748b" }}>/ hr</span>
            </div>
          )}
        </div>

        {/* Status indicator: Critical or Off */}
        {isCritical ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#dc2626",
              fontSize: 10,
              fontWeight: 800,
              padding: "1px 6px",
              borderRadius: 999,
              marginTop: 3,
              width: "fit-content",
            }}
            title={`CRITICAL: Exceeding rated capacity (${node.rated_power} kW)!`}
          >
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#ef4444" }} />
            <span>Critical {healthPercent ? `(${healthPercent.toFixed(0)}%)` : ""}</span>
          </span>
        ) : node.is_off ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              background: "#f1f5f9",
              color: "#64748b",
              fontSize: 9.5,
              fontWeight: 700,
              padding: "1px 6px",
              borderRadius: 999,
              marginTop: 3,
              width: "fit-content",
            }}
          >
            Off
          </span>
        ) : null}

        {/* Distribution Share Badge when in Distribution mode */}
        {distributionMode && !isRoot && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
              background: "#f0fdfa",
              border: "1px solid #99f6e4",
              color: "#0f766e",
              fontSize: 9.5,
              fontWeight: 700,
              padding: "1px 6px",
              borderRadius: 999,
              marginTop: 3,
              fontVariantNumeric: "tabular-nums",
            }}
            title={`${totalPower > 0 ? ((Math.abs(node.power) / totalPower) * 100).toFixed(1) : 0}% of total power`}
          >
            <span>⚡ {totalPower > 0 ? ((Math.abs(node.power) / totalPower) * 100).toFixed(1) : 0}%</span>
          </div>
        )}

        {/* Expand / Collapse Pill */}
        {canToggle && (
          <button
            type="button"
            onClick={onToggleClick}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
              background: isOpen ? "#f8fafc" : "#e0f2fe",
              border: `1px solid ${isOpen ? "#e2e8f0" : "#bae6fd"}`,
              color: isOpen ? "#475569" : "#0284c7",
              fontSize: 10,
              fontWeight: 700,
              padding: "2px 8px",
              borderRadius: 999,
              cursor: "pointer",
              lineHeight: 1.2,
              marginTop: 4,
              width: "fit-content",
              transition: "all 0.15s ease",
            }}
            title={isOpen ? "Collapse branch" : `Expand ${node.counts.total} sub-node${node.counts.total > 1 ? "s" : ""}`}
          >
            <span style={{ fontSize: 9 }}>{isOpen ? "▾" : "▸"}</span>
            <span>{isOpen ? "Collapse" : `Expand${node.counts.total > 0 ? ` (${node.counts.total})` : ""}`}</span>
          </button>
        )}
      </div>
    </div>
  );
};

// ===== Node box =====
// Every card in the tree shows the same handful of fields, so repeating the
// field help on all of them just litters the diagram. Only the root card
// carries it; per-node figures are explained in the details panel instead.
const NoHelp = ({ children }) => <>{children}</>;

const NodeBox = ({
  node,
  isRoot,
  canToggle,
  isOpen,
  onBoxClick,
  onToggleClick,
  unitPrice = 0,
  distributionMode = false,
  onToggleDistribution,
  totalPower = 0,
}) => {
  const Help = isRoot ? FieldHelp : NoHelp;
  const isCritical = node.status_key === "Critical";
  const stripeColor = node.is_off
    ? STATUS_COLORS.OFF
    : node.status_key
      ? STATUS_COLORS[node.status_key]
      : "#cbd5e1";

  const badge = CATEGORY_BADGE[node.category];

  const showPower = !node.is_virtual || Math.abs(node.power) > 0.001;
  const clickable = !node.is_virtual;

  // Tint the whole box with the health-status color (skip virtual nodes)
  const statusBg =
    !node.is_virtual && (node.is_off || node.status_key)
      ? STATUS_BG[node.is_off ? "OFF" : node.status_key]
      : null;

  return (
    <div
      data-node-box={node.meter_id || node.id}
      style={{
        ...(isRoot ? styles.parentBox : styles.childBox),
        opacity: node.is_stale ? 0.65 : 1,
        cursor: clickable ? "pointer" : "default",
        borderColor: isCritical ? "#ef4444" : stripeColor,
        ...(statusBg ? { background: statusBg } : {}),
        // Clear, professional critical indicator with alert border and soft glow (no confusing rapid full-card pulse)
        ...(isCritical
          ? {
              borderWidth: 2,
              boxShadow: "0 0 0 3px rgba(239, 68, 68, 0.2), 0 6px 16px rgba(239, 68, 68, 0.15)",
              backgroundColor: "#fef2f2",
            }
          : {}),
      }}
      onClick={clickable ? onBoxClick : undefined}
      onMouseEnter={(e) => {
        if (clickable) {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = isCritical
            ? "0 0 0 3px rgba(239, 68, 68, 0.25), 0 8px 20px rgba(239, 68, 68, 0.2)"
            : "0 6px 16px rgba(15,23,42,0.12), 0 2px 4px rgba(15,23,42,0.06)";
        }
      }}
      onMouseLeave={(e) => {
        if (clickable) {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = isCritical
            ? "0 0 0 3px rgba(239, 68, 68, 0.2), 0 6px 16px rgba(239, 68, 68, 0.15)"
            : isRoot
              ? "0 4px 14px rgba(15,23,42,0.10), 0 2px 4px rgba(15,23,42,0.04)"
              : "0 1px 3px rgba(15,23,42,0.08), 0 1px 2px rgba(15,23,42,0.04)";
        }
      }}
    >
      {/* Stripe */}
      <div
        style={
          isRoot
            ? { ...styles.parentTopStripe, background: isCritical ? "#ef4444" : stripeColor }
            : { ...styles.childLeftStripe, background: isCritical ? "#ef4444" : stripeColor }
        }
      />

      {/* Header row */}
      <div style={styles.boxHeader}>
        <div style={styles.boxNameWrap}>
          <span
            style={{
              ...styles.boxName,
              fontSize: isRoot ? 14 : 12,
              color: node.is_virtual ? "#64748b" : "#0f172a",
            }}
          >
            {node.name}
          </span>
          {badge && (
            <span
              style={{
                ...styles.catBadge,
                background: badge.bg,
                color: badge.fg,
              }}
            >
              {node.explains_category ? (
                <FieldHelp id="live.nodeCategory">{badge.label}</FieldHelp>
              ) : (
                badge.label
              )}
            </span>
          )}
        </div>

        {/* Stable Distribution Toggle on Root Node Header */}
        {isRoot && (
          <DistributionToggle active={distributionMode} onToggle={onToggleDistribution} />
        )}

        {node.is_stale && (
          <span style={styles.staleBadge}>
            <Help id="live.offlineBadge">offline</Help>
          </span>
        )}
        {node.is_exporting && (
          <span style={styles.exportBadge}>
            <Help id="live.exportBadge">↑ EXPORT</Help>
          </span>
        )}
      </div>

      {/* Power row: Power kW + PKR / hr */}
      {showPower && (
        <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: 4, flexWrap: "wrap" }}>
          <span
            style={{
              ...styles.boxPower,
              fontSize: isRoot ? 22 : 16,
              color: node.is_exporting
                ? "#10b981"
                : node.is_off
                  ? "#94a3b8"
                  : "#0f172a",
            }}
          >
            <Help id="live.nodePower">{Math.abs(node.power).toFixed(2)}</Help>
          </span>
          <span style={styles.boxPowerUnit}>kW</span>

          {/* Operating Cost / hr on Topmost Root Node right next to kW value */}
          {isRoot && (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: "11px",
                color: "#0f766e",
                background: "#f0fdfa",
                border: "1px solid #ccfbf1",
                padding: "2px 8px",
                borderRadius: "6px",
                fontWeight: 600,
                marginLeft: 4,
                fontVariantNumeric: "tabular-nums",
              }}
              title={unitPrice > 0 ? `Estimated operating cost: PKR ${unitPrice} per kWh` : "Unit price not set in settings"}
            >
              <span style={{ fontSize: "10px", color: "#14b8a6", fontWeight: 700 }}>PKR</span>
              <span>
                {unitPrice > 0 && Math.abs(node.power) > 0
                  ? (Math.abs(node.power) * unitPrice).toLocaleString(undefined, {
                      maximumFractionDigits: 0,
                    })
                  : "--"}
              </span>
              <span style={{ fontSize: "9px", color: "#64748b" }}>/ hr</span>
            </div>
          )}
        </div>
      )}

      {/* Distribution Share Badge when in Distribution mode */}
      {distributionMode && !isRoot && (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            background: "#f0fdfa",
            border: "1px solid #99f6e4",
            color: "#0f766e",
            fontSize: 10,
            fontWeight: 700,
            padding: "2px 7px",
            borderRadius: 999,
            marginBottom: 4,
            fontVariantNumeric: "tabular-nums",
          }}
          title={`${totalPower > 0 ? ((Math.abs(node.power) / totalPower) * 100).toFixed(1) : 0}% of total power (${Math.abs(node.power).toFixed(2)} kW of ${totalPower.toFixed(2)} kW)`}
        >
          <span style={{ fontSize: 9 }}>⚡</span>
          <span>{totalPower > 0 ? ((Math.abs(node.power) / totalPower) * 100).toFixed(1) : 0}% of Total</span>
        </div>
      )}

      {/* Insight Value */}
      {node.insight_value !== null && node.insight_value !== undefined && (
        <div style={{ fontSize: 10, color: "#475569", background: "#f1f5f9", padding: "2px 6px", borderRadius: 4, width: "fit-content", marginTop: 4, fontWeight: 500 }}>
          <Help id="live.insightValue">Insight</Help>: {node.insight_value}
        </div>
      )}

      {/* Footer */}
      <div style={styles.boxFooter}>
        {node.is_off ? (
          <span style={{ ...styles.statusBadge, ...statusPill("OFF") }}>
            <Help id="live.offStatus">Off</Help>
          </span>
        ) : isCritical ? (
          <span
            style={{
              ...styles.statusBadge,
              ...statusPill("Critical"),
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
              fontWeight: 800,
            }}
            title="CRITICAL: Running above rated capacity! Needs immediate attention."
          >
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#ef4444", display: "inline-block" }} />
            <span>Critical ({Number(node.health_percent).toFixed(0)}%)</span>
          </span>
        ) : node.status_key ? (
          <span style={{ ...styles.statusBadge, ...statusPill(node.status_key) }}>
            <Help
              help={{
                ...getFieldHelp("live.healthStatus"),
                title: `Health: ${node.status_key}`,
                use: `${HEALTH_TOOLTIPS[node.status_key] || node.status_key} ${getFieldHelp("live.healthStatus")?.use || ""}`,
              }}
            >
              {`${Number(node.health_percent).toFixed(1)}%`}
            </Help>
          </span>
        ) : node.is_virtual ? null : (
          <span style={{ ...styles.statusBadge, ...statusPill("Unknown") }}>
            <Help id="live.healthStatus">–</Help>
          </span>
        )}

        {/* Small Expand Pill replacing cryptic 7/5/2 numbers */}
        {canToggle && (
          <button
            type="button"
            onClick={onToggleClick}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
              background: isOpen ? "#f8fafc" : "#e0f2fe",
              border: `1px solid ${isOpen ? "#e2e8f0" : "#bae6fd"}`,
              color: isOpen ? "#475569" : "#0284c7",
              fontSize: 10,
              fontWeight: 700,
              padding: "2px 8px",
              borderRadius: 999,
              cursor: "pointer",
              lineHeight: 1.2,
              fontVariantNumeric: "tabular-nums",
              transition: "all 0.15s ease",
            }}
            title={isOpen ? "Collapse branch" : `Expand ${node.counts.total} sub-node${node.counts.total > 1 ? "s" : ""}`}
          >
            <span style={{ fontSize: 9 }}>{isOpen ? "▾" : "▸"}</span>
            <span>{isOpen ? "Collapse" : `Expand${node.counts.total > 0 ? ` (${node.counts.total})` : ""}`}</span>
          </button>
        )}
      </div>
    </div>
  );
};

// ===== Details Panel (side popup, ported from old LiveTab diagram) =====
const DetailsPanel = ({ node, pos, totalPower, viewMode = "tabular", onViewModeChange, onClose, onAssignRatedPower }) => {
  const reading = node.reading;
  const phases = reading?.phases || null;
  const phaseType = reading?.phase_type || null;

  const headerColor =
    node.category === "GRID"
      ? "#3b82f6"
      : node.category === "SOLAR"
        ? "#f59e0b"
        : node.category === "GENERATOR"
          ? "#8b5cf6"
          : node.status_key === "Normal"
            ? "#22c55e"
            : node.status_key === "Warning"
              ? "#f59e0b"
              : node.status_key === "Critical"
                ? "#ef4444"
                : node.is_off
                  ? "#94a3b8"
                  : "#16a34a";

  const isSource = ["GRID", "SOLAR", "GENERATOR"].includes(node.category);
  const status = node.is_off ? "OFF" : "ON";
  const typeLabel = isSource ? node.category : "LOAD";

  return (
    <div
      data-hv-panel="1"
      style={{
        ...styles.detailsPanel,
        top: pos.top,
        left: pos.left,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Values are normal weight by default so Power (inline bold) stands out. */}
      <style>{`[data-hv-panel] strong{font-weight:400;}`}</style>
      <div style={{ ...styles.detailsHeader, background: headerColor }}>
        <span>{node.name}</span>
        <button
          onClick={onClose}
          style={{
            background: "transparent",
            border: "none",
            color: "#cbd5e1",
            fontSize: 20,
            cursor: "pointer",
          }}
        >
          ×
        </button>
      </div>
      <div
        style={{
          padding: "14px 16px",
          maxHeight: 500,
          overflowY: "auto",
          overscrollBehavior: "contain",
        }}
      >
        {/* Toggle between Tabular View and Linear View */}
        <div style={{
          display: "flex",
          background: "#f1f5f9",
          borderRadius: 6,
          padding: 2,
          gap: 2,
          marginBottom: 12,
        }}>
          <button
            type="button"
            onClick={() => onViewModeChange && onViewModeChange("tabular")}
            style={{
              flex: 1,
              padding: "5px 8px",
              fontSize: 12,
              fontWeight: 700,
              color: "#0f172a",
              background: "#ffffff",
              border: "none",
              borderRadius: 4,
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              cursor: "pointer",
            }}
          >
            <FieldHelp id="live.tabularView">Tabular View</FieldHelp>
          </button>
          <button
            type="button"
            onClick={() => onViewModeChange && onViewModeChange("linear")}
            style={{
              flex: 1,
              padding: "5px 8px",
              fontSize: 12,
              fontWeight: 500,
              color: "#64748b",
              background: "transparent",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
            }}
          >
            <FieldHelp id="live.linearView">Linear View</FieldHelp>
          </button>
        </div>

        {/* 2-Column Horizontal Layout: Left = General & Parameters, Right = 3-Phase Details */}
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          {/* Left Column: General & Parameters */}
          <div style={{ flex: "1 1 250px", minWidth: 230 }}>
            <div style={styles.detailRow}>
              <span><FieldHelp id="live.nodeType">Type</FieldHelp>:</span>
              <strong style={{ textTransform: "uppercase" }}>{typeLabel}</strong>
            </div>
            {node.is_multi_parent && (
              <div style={styles.detailRow}>
                <span><FieldHelp id="live.multiParent">Parents</FieldHelp>:</span>
                <strong style={{ fontWeight: 600, color: "#2563eb" }}>
                  {Array.isArray(node.parent_meter_ids) && node.parent_meter_ids.length > 0
                    ? `${node.parent_meter_ids.length} Assigned Parents`
                    : "Multiple Parents"}
                </strong>
              </div>
            )}
            <div style={styles.detailRow}>
              <span><FieldHelp id="live.onOffStatus">Status</FieldHelp>:</span>
              <span
                style={{
                  padding: "2px 8px",
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 600,
                  background: status === "ON" ? "#dcfce7" : "#fee2e2",
                  color: status === "ON" ? "#16a34a" : "#dc2626",
                }}
              >
                {status}
              </span>
            </div>
            {node.health_status ? (
              <div style={styles.detailRow}>
                <span><FieldHelp id="live.healthStatus">Health</FieldHelp>:</span>
                <strong
                  style={{
                    color:
                      node.health_status === "Normal"
                        ? "#16a34a"
                        : node.health_status === "Warning"
                          ? "#f59e0b"
                          : node.health_status === "Critical"
                            ? "#ef4444"
                            : "#64748b",
                  }}
                >
                  {node.health_status}
                </strong>
              </div>
            ) : needsRatedPowerAssignment({
              isVirtual: node.is_virtual,
              ratedPower: node.rated_power,
            }) ? (
              <div style={styles.detailRow}>
                <span><FieldHelp id="live.healthStatus">Health</FieldHelp>:</span>
                <button
                  type="button"
                  onClick={() =>
                    onAssignRatedPower?.({
                      meterId: node.meter_id,
                      name: node.name,
                    })
                  }
                  disabled={!onAssignRatedPower}
                  title="Opens Node Settings for this machine"
                  style={{
                    background: "#fffbeb",
                    border: "1px dashed #f59e0b",
                    borderRadius: 4,
                    padding: "2px 8px",
                    color: "#b45309",
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: onAssignRatedPower ? "pointer" : "default",
                  }}
                >
                  Assign rated power
                </button>
              </div>
            ) : null}

            {/* Parameters */}
            <div style={styles.detailSection}>
              <div style={styles.detailSectionTitle}>Parameters</div>
              <div style={styles.detailRow}>
                <span><FieldHelp id="live.power">Power</FieldHelp>:</span>
                <strong style={{ fontWeight: 800 }}>
                  {Math.abs(node.power).toFixed(2)} kW
                </strong>
              </div>
              {node.insight_value !== null && node.insight_value !== undefined && (
                <div style={styles.detailRow}>
                  <span><FieldHelp id="live.insightValue">Insight Value</FieldHelp>:</span>
                  <strong>{node.insight_value}</strong>
                </div>
              )}
              <div style={styles.detailRow}>
                <span><FieldHelp id="live.voltage">Voltage</FieldHelp>:</span>
                <strong>
                  {reading?.voltage != null
                    ? reading.voltage.toFixed(2) + " V"
                    : "-"}
                </strong>
              </div>
              <div style={styles.detailRow}>
                <span><FieldHelp id="live.current">Current</FieldHelp>:</span>
                <strong>
                  {reading?.current != null
                    ? reading.current.toFixed(1) + " A"
                    : "-"}
                </strong>
              </div>
              <div style={styles.detailRow}>
                <span><FieldHelp id="live.powerFactor">Power Factor</FieldHelp>:</span>
                <strong style={{ color: pfColor(reading?.power_factor) }}>
                  {reading?.power_factor != null
                    ? reading.power_factor.toFixed(2)
                    : "-"}
                </strong>
              </div>
              <div style={styles.detailRow}>
                <span><FieldHelp id="live.frequency">Frequency</FieldHelp>:</span>
                <strong>
                  {reading?.frequency != null
                    ? reading.frequency.toFixed(2) + " Hz"
                    : "-"}
                </strong>
              </div>
              <div style={styles.detailRow}>
                <span><FieldHelp id="live.thdVoltage">THD Voltage</FieldHelp>:</span>
                <strong>
                  {reading?.thd_voltage != null
                    ? reading.thd_voltage.toFixed(2) + " %"
                    : "-"}
                </strong>
              </div>
              <div style={styles.detailRow}>
                <span><FieldHelp id="live.thdCurrent">THD Current</FieldHelp>:</span>
                <strong>
                  {reading?.thd_current != null
                    ? reading.thd_current.toFixed(2) + " %"
                    : "-"}
                </strong>
              </div>
              <div style={styles.detailRow}>
                <span><FieldHelp id="live.tap">TAP</FieldHelp>:</span>
                <strong>
                  {reading?.tap != null ? reading.tap.toFixed(2) + " kW" : "-"}
                </strong>
              </div>
              <div style={styles.detailRow}>
                <span><FieldHelp id="live.shareOfTotal">% of Total Load</FieldHelp>:</span>
                <strong>
                  {totalPower > 0
                    ? ((Math.abs(node.power) / totalPower) * 100).toFixed(1)
                    : 0}
                  %
                </strong>
              </div>
            </div>

            {/* Timestamp */}
            {reading?.timestamp && (
              <div style={styles.detailSection}>
                <div style={styles.detailRow}>
                  <span><FieldHelp id="live.lastUpdated">Last Updated</FieldHelp>:</span>
                  <strong style={{ fontSize: 11, color: "#64748b" }}>
                    {reading.timestamp}
                  </strong>
                </div>
              </div>
            )}
          </div>

          {/* Right Column: 3-Phase Details displayed horizontally next to parameters */}
          {phases && phaseType === 3 ? (
            <div style={{ flex: "1 1 250px", minWidth: 230, borderLeft: "1px solid #e2e8f0", paddingLeft: 14 }}>
              <div style={styles.detailSectionTitle}>
                <FieldHelp id="live.phaseDetails">Phase Details</FieldHelp>
              </div>
              {["phase1", "phase2", "phase3"].map((pk, i) => {
                const p = phases[pk];
                if (!p) return null;
                const phaseColor = i === 0 ? "#ef4444" : i === 1 ? "#eab308" : "#3b82f6";
                return (
                  <div
                    key={pk}
                    style={{
                      marginBottom: 8,
                      padding: "8px 10px",
                      background: "#f8fafc",
                      borderRadius: 8,
                      border: "1px solid #f1f5f9",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: phaseColor,
                        marginBottom: 4,
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: phaseColor }} />
                      Phase {i + 1}
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "2px 10px",
                        fontSize: 11,
                      }}
                    >
                      <span style={{ color: "#64748b" }}>Voltage:</span>
                      <strong style={{ color: "#334155" }}>
                        {p.voltage != null ? p.voltage.toFixed(1) + " V" : "-"}
                      </strong>
                      <span style={{ color: "#64748b" }}>Current:</span>
                      <strong style={{ color: "#334155" }}>
                        {p.current != null ? p.current.toFixed(1) + " A" : "-"}
                      </strong>
                      <span style={{ color: "#64748b" }}>Power:</span>
                      <strong style={{ color: "#334155" }}>
                        {p.power != null ? p.power.toFixed(2) + " kW" : "-"}
                      </strong>
                      <span style={{ color: "#64748b" }}>PF:</span>
                      <strong style={{ color: "#334155" }}>
                        {p.power_factor != null ? p.power_factor.toFixed(2) : "-"}
                      </strong>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ flex: "1 1 200px", minWidth: 180, borderLeft: "1px solid #e2e8f0", paddingLeft: 14, display: "flex", flexDirection: "column", justifyContent: "center", minHeight: 180, color: "#94a3b8", fontSize: 12, textAlign: "center" }}>
              <span style={{ fontWeight: 600, color: "#64748b" }}>Single Phase Unit</span>
              <span style={{ fontSize: 11, marginTop: 4 }}>All readings on single channel</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const statusPill = (key) => {
  switch (key) {
    case "Normal":
      return { background: "#dcfce7", color: "#166534" };
    case "Warning":
      return { background: "#fef3c7", color: "#92400e" };
    case "Critical":
      return { background: "#fee2e2", color: "#991b1b" };
    case "OFF":
      return { background: "#f1f5f9", color: "#64748b" };
    case "Virtual":
      return { background: "#f1f5f9", color: "#475569" };
    default:
      return { background: "#f1f5f9", color: "#94a3b8" };
  }
};

// ===== Styles =====
const styles = {

  container: {
    background: "#f8fafc",
    borderRadius: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    width: "100%",
    height: "calc(100vh - 65px)",
  },
  empty: {
    textAlign: "center",
    padding: 60,
    color: "#64748b",
    fontSize: 14,
  },

  // Header
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "linear-gradient(135deg, #0f766e, #0d9488)",
    color: "#fff",
    padding: "10px 14px",
    borderRadius: 10,
    marginBottom: 10,
    flexWrap: "wrap",
    gap: 8,
  },
  headerLeftGroup: {
    display: "flex",
    alignItems: "flex-end",
    gap: 28,
    flexWrap: "wrap",
  },
  headerLeft: { display: "flex", flexDirection: "column" },
  headerCost: { display: "flex", flexDirection: "column" },
  headerCostValue: {
    fontSize: 22,
    fontWeight: 700,
    lineHeight: 1.1,
    fontVariantNumeric: "tabular-nums",
  },
  headerCostCurrency: { fontSize: 12, opacity: 0.85, fontWeight: 500 },
  headerLabel: {
    fontSize: 10,
    letterSpacing: 0.8,
    opacity: 0.85,
    textTransform: "uppercase",
  },
  headerValue: { fontSize: 22, fontWeight: 700, lineHeight: 1.1 },
  headerUnit: { fontSize: 12, fontWeight: 500, opacity: 0.9 },
  headerSub: { fontSize: 11, opacity: 0.8, marginTop: 2 },
  headerCounts: { display: "flex", gap: 6, flexWrap: "wrap" },
  countChip: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    background: "rgba(255,255,255,0.15)",
    border: "1px solid rgba(255,255,255,0.2)",
    padding: "4px 8px",
    borderRadius: 999,
    fontSize: 11,
  },
  countDot: { width: 7, height: 7, borderRadius: "50%", display: "inline-block" },
  countLabel: { color: "#fff", opacity: 0.9 },
  countNum: { fontSize: 12, fontWeight: 700 },
  headerActions: { display: "flex", gap: 6 },
  actionBtn: {
    background: "rgba(255,255,255,0.15)",
    border: "1px solid rgba(255,255,255,0.25)",
    color: "#fff",
    padding: "4px 10px",
    borderRadius: 6,
    fontSize: 11,
    cursor: "pointer",
  },

  // Zoomable pane wrapper
  zoomOuter: {
    position: "relative",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  zoomBar: {
    position: "absolute",
    bottom: 20,
    left: 20,
    zIndex: 15,
    display: "flex",
    alignItems: "center",
    gap: 4,
    background: "#fff",
    border: "1px solid #cbd5e1",
    borderRadius: 999,
    padding: "4px 8px",
    boxShadow: "0 4px 16px rgba(15,23,42,0.12)",
  },
  zoomBtn: {
    width: 26,
    height: 26,
    border: "none",
    borderRadius: 999,
    background: "transparent",
    cursor: "pointer",
    fontSize: 16,
    fontWeight: 700,
    color: "#0f172a",
  },
  zoomBtnText: { width: "auto", padding: "0 8px", fontSize: 11, fontWeight: 600 },
  zoomPct: {
    fontSize: 11,
    fontWeight: 600,
    color: "#475569",
    minWidth: 36,
    textAlign: "center",
    fontVariantNumeric: "tabular-nums",
  },

  // Chart container
  chartWrap: {
    background: "#f8fafc",
    borderRadius: 0,
    padding: "75px 14px 40px 14px",
    border: "none",
    overflow: "hidden",
    cursor: "grab",
    userSelect: "none",
    flex: 1,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    minHeight: 0,
  },

  // Org node recursive wrapper
  orgNodeWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minWidth: "fit-content",
  },

  vLineDown: {
    width: 2,
    height: 18,
    background: "#cbd5e1",
  },
  hLineWrap: {
    position: "relative",
    width: "100%",
    height: 0,
  },
  hLine: {
    position: "absolute",
    top: 0,
    height: 2,
    background: "#cbd5e1",
  },
  childrenRow: {
    display: "flex",
    gap: 10,
    width: "100%",
    flexWrap: "nowrap",
    alignItems: "flex-start",
    position: "relative",
  },
  childCol: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flex: "0 0 auto",
  },
  vStub: {
    width: 2,
    height: 14,
    background: "#cbd5e1",
  },

  // Node boxes
  parentBox: {
    background: "#fff",
    borderRadius: 10,
    border: "2px solid",
    padding: "12px 16px 10px",
    minWidth: 200,
    maxWidth: 320,
    boxShadow: "0 4px 14px rgba(15,23,42,0.10), 0 2px 4px rgba(15,23,42,0.04)",
    position: "relative",
    overflow: "hidden",
    transition: "transform 0.12s, box-shadow 0.12s",
  },
  parentTopStripe: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 4,
  },
  childBox: {
    background: "#fff",
    borderRadius: 9,
    border: "1px solid #e2e8f0",
    padding: "8px 10px 7px 14px",
    minWidth: 160,
    maxWidth: 200,
    boxShadow: "0 1px 3px rgba(15,23,42,0.08), 0 1px 2px rgba(15,23,42,0.04)",
    position: "relative",
    overflow: "hidden",
    transition: "transform 0.12s, box-shadow 0.12s",
  },
  childLeftStripe: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 4,
  },

  boxHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 6,
    flexWrap: "wrap",
    marginBottom: 5,
  },
  boxNameWrap: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    flexWrap: "wrap",
  },
  boxName: {
    fontWeight: 700,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 150,
  },
  catBadge: {
    fontSize: 9,
    fontWeight: 700,
    padding: "1px 6px",
    borderRadius: 999,
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  staleBadge: {
    fontSize: 9,
    background: "#fee2e2",
    color: "#991b1b",
    padding: "1px 6px",
    borderRadius: 999,
    fontWeight: 600,
  },
  exportBadge: {
    fontSize: 9,
    background: "#d1fae5",
    color: "#065f46",
    padding: "1px 6px",
    borderRadius: 999,
    fontWeight: 700,
    letterSpacing: 0.3,
  },
  boxPowerRow: {
    display: "flex",
    alignItems: "baseline",
    gap: 4,
    marginBottom: 4,
  },
  boxPower: {
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
  },
  boxPowerUnit: { fontSize: 11, color: "#64748b", fontWeight: 500 },

  boxMetrics: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    fontSize: 10,
    color: "#64748b",
    fontVariantNumeric: "tabular-nums",
    marginBottom: 6,
  },
  metric: { display: "inline-flex", gap: 3, alignItems: "center" },
  metricLabel: {
    fontSize: 8,
    color: "#94a3b8",
    fontWeight: 700,
    textTransform: "uppercase",
  },

  boxFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },
  statusBadge: {
    display: "inline-block",
    fontSize: 10,
    fontWeight: 700,
    padding: "2px 7px",
    borderRadius: 999,
  },
  toggleBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    color: "#475569",
    fontSize: 10,
    fontWeight: 700,
    padding: "2px 8px",
    borderRadius: 999,
    cursor: "pointer",
    lineHeight: 1.2,
    fontVariantNumeric: "tabular-nums",
  },

  // ----- Details Panel -----
  detailsPanel: {
    position: "fixed",
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
    width: 560,
    maxWidth: "92vw",
    border: "1px solid #e2e8f0",
    zIndex: 1000,
    overscrollBehavior: "contain",
  },
  detailsHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px",
    background: "#1e293b",
    borderRadius: "12px 12px 0 0",
    color: "#fff",
    fontWeight: 700,
    fontSize: 14,
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 10,
    fontSize: 13,
    color: "#64748b",
    gap: 12,
  },
  detailSection: {
    marginTop: 10,
    paddingTop: 10,
    borderTop: "1px solid #e2e8f0",
  },
  detailSectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#475569",
    marginBottom: 6,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
};

export default HierarchyTreeView;
